import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CreditCard, MapPin, User, ShoppingBag, Truck, Loader2, FileText } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { ordersAPI, paymentAPI } from '../services/api';
import toast from 'react-hot-toast';

const Checkout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { items: cartItems, totalPrice, clearCart } = useCart();
  const { user, updateProfile, isAuthenticated } = useAuth();
  const { settings } = useSettings();
  
  const [loading, setLoading] = useState(false);
  const [orderCompleted, setOrderCompleted] = useState(false);

  // URL'den hata parametresi kontrolü
  useEffect(() => {
    const error = searchParams.get('error');
    const orderId = searchParams.get('orderId');
    
    if (error === 'payment_failed') {
      toast.error('Ödeme işlemi başarısız oldu. Lütfen tekrar deneyin.');
      // URL'den parametreleri temizle
      navigate('/checkout', { replace: true });
    }
  }, [searchParams, navigate]);
  
  const [useSameBillingAddress, setUseSameBillingAddress] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Türkiye',
    billingName: '',
    billingPhone: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZipCode: '',
    billingCountry: 'Türkiye',
    notes: '',
    deliveryMethod: 'standard'
  });

  // Sepet kontrolü (sadece sayfa ilk yüklendiğinde ve sipariş oluşturulmuyorken)
  useEffect(() => {
    // Sipariş tamamlandıysa sepete yönlendirme yapma
    if (orderCompleted) {
      return;
    }
    if (!loading && (!cartItems || cartItems.length === 0)) {
      navigate('/cart');
      return;
    }
  }, [cartItems, navigate, loading, orderCompleted]);

  // Form verilerini giriş yapmış kullanıcılar için doldur
  useEffect(() => {
    if (isAuthenticated && user && (!formData.firstName && !formData.email)) {
      // user.name'i ad ve soyada ayır
      const nameParts = (user.name || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const hasBilling = Boolean(user.billing_street || user.billing_city);
      setUseSameBillingAddress(!hasBilling);
      setFormData(prev => ({
        ...prev,
        firstName: firstName,
        lastName: lastName,
        email: user.email || '',
        phone: user.phone || '',
        address: user.address_street || '',
        city: user.address_city || '',
        state: user.address_state || '',
        zipCode: user.address_zip_code || '',
        country: user.address_country || 'Türkiye',
        billingName: user.billing_name || '',
        billingPhone: user.billing_phone || user.phone || '',
        billingAddress: user.billing_street || '',
        billingCity: user.billing_city || '',
        billingState: user.billing_state || '',
        billingZipCode: user.billing_zip_code || '',
        billingCountry: user.billing_country || 'Türkiye'
      }));
    }
  }, [isAuthenticated, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    if (!formData.address || !formData.city || !formData.state) {
      toast.error('Lütfen teslimat adres bilgilerini tamamlayın');
      return;
    }

    if (!useSameBillingAddress) {
      if (!formData.billingName || !formData.billingAddress || !formData.billingCity || !formData.billingState) {
        toast.error('Lütfen fatura adres bilgilerini tamamlayın');
        return;
      }
    }

    setLoading(true);

    try {
      // Kargo ücreti hesapla
      let shippingCost = 0;
      if (formData.deliveryMethod === 'express') {
        shippingCost = settings.shipping?.expressShippingCost || 249;
      } else if (formData.deliveryMethod === 'standard') {
        const threshold = settings.shipping?.freeShippingThreshold || 300;
        const defaultCost = settings.shipping?.defaultShippingCost || 50;
        shippingCost = totalPrice < threshold ? defaultCost : 0;
      }
      // KDV fiyata dahil, ekstra eklenmeyecek
      const finalTotal = totalPrice + shippingCost;

      const shippingName = `${formData.firstName} ${formData.lastName}`;
      const shippingAddress = {
        name: shippingName,
        email: formData.email || user?.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        country: formData.country
      };

      const billingAddress = useSameBillingAddress
        ? shippingAddress
        : {
            name: formData.billingName,
            phone: formData.billingPhone || formData.phone,
            address: formData.billingAddress,
            city: formData.billingCity,
            state: formData.billingState,
            zipCode: formData.billingZipCode,
            country: formData.billingCountry || 'Türkiye'
          };

      const orderData = {
        items: (cartItems || []).map(item => ({
          product: item.product_id,
          quantity: item.quantity
        })),
        shippingAddress,
        billingAddress,
        paymentMethod: 'kredi_karti',
        notes: formData.notes,
        deliveryMethod: formData.deliveryMethod
      };

      // Önce siparişi oluştur
      const response = isAuthenticated 
        ? await ordersAPI.createOrder(orderData)
        : await ordersAPI.createGuestOrder(orderData);

      const order = response.data.order;

      // Giriş yapmış kullanıcılar için adres bilgilerini profile'a kaydet
      if (isAuthenticated) {
        try {
          const profileData = {
            name: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
            phone: formData.phone,
            address_street: formData.address,
            address_city: formData.city,
            address_state: formData.state,
            address_zip_code: formData.zipCode,
            address_country: formData.country,
            ...(!useSameBillingAddress
              ? {
                  billing_name: formData.billingName,
                  billing_phone: formData.billingPhone || formData.phone,
                  billing_street: formData.billingAddress,
                  billing_city: formData.billingCity,
                  billing_state: formData.billingState,
                  billing_zip_code: formData.billingZipCode,
                  billing_country: formData.billingCountry
                }
              : {})
          };
          await updateProfile(profileData);
        } catch (profileError) {
          // Profile güncellenemese bile sipariş devam etsin
        }
      }

      const baseUrl = window.location.origin;
      const paymentInitData = {
        orderId: order.id,
        amount: parseFloat(finalTotal).toFixed(2),
        successUrl: `${baseUrl}/order-success/${order.id}`,
        failUrl: `${baseUrl}/checkout?error=payment_failed&orderId=${order.id}`,
        customerInfo: {
          name: `${formData.firstName} ${formData.lastName}`,
          surname: formData.lastName || '',
          email: formData.email || user?.email,
          phone: formData.phone
        }
      };

      const paymentResponse = await paymentAPI.initializeIyzico(paymentInitData);
      
      if (paymentResponse.data.success) {
        setOrderCompleted(true);
        if (paymentResponse.data.paymentPageUrl) {
          window.location.href = paymentResponse.data.paymentPageUrl;
          return;
        } else if (paymentResponse.data.checkoutFormContent) {
          const div = document.createElement('div');
          div.innerHTML = paymentResponse.data.checkoutFormContent;
          document.body.appendChild(div);
          div.querySelector('form')?.submit();
          return;
        } else {
          throw new Error('Ödeme sayfası oluşturulamadı');
        }
      } else {
        throw new Error(paymentResponse.data.message || 'Ödeme başlatılamadı');
      }
      
    } catch (error) {
      console.error('Sipariş oluşturma hatası:', error);
      toast.error(error.response?.data?.message || 'Sipariş oluşturulamadı');
      setLoading(false);
    }
  };

  if (!cartItems || cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <ShoppingBag className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Sepetiniz Boş</h2>
            <p className="text-gray-600 mb-6">Sipariş vermek için önce sepete ürün ekleyin.</p>
            <button
              onClick={() => navigate('/products')}
              className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors"
            >
              Alışverişe Devam Et
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Sipariş Özeti</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Sol Taraf - Form */}
          <div className="space-y-6">
            {/* Kişisel Bilgiler */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-4">
                <User className="w-5 h-5 text-orange-500 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">Kişisel Bilgiler</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ad *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Soyad *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">E-posta *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefon *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Adres Bilgileri */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-4">
                <MapPin className="w-5 h-5 text-orange-500 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">Teslimat Adresi</h2>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adres *</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Şehir *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">İlçe *</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Posta Kodu</label>
                    <input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Fatura Adresi */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <FileText className="w-5 h-5 text-orange-500 mr-2" />
                  <h2 className="text-xl font-semibold text-gray-900">Fatura Adresi</h2>
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer mb-4">
                <input
                  type="checkbox"
                  checked={useSameBillingAddress}
                  onChange={(e) => setUseSameBillingAddress(e.target.checked)}
                  className="mt-1"
                />
                <span className="text-sm text-gray-700">
                  Fatura adresim teslimat adresimle aynı
                </span>
              </label>

              {!useSameBillingAddress ? (
                    <div className="space-y-4 border-t border-gray-100 pt-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Fatura adı / unvan *</label>
                          <input
                            type="text"
                            name="billingName"
                            value={formData.billingName}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            required={!useSameBillingAddress}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                          <input
                            type="tel"
                            name="billingPhone"
                            value={formData.billingPhone}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Adres *</label>
                        <textarea
                          name="billingAddress"
                          value={formData.billingAddress}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          required={!useSameBillingAddress}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Şehir *</label>
                          <input
                            type="text"
                            name="billingCity"
                            value={formData.billingCity}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            required={!useSameBillingAddress}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">İlçe *</label>
                          <input
                            type="text"
                            name="billingState"
                            value={formData.billingState}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            required={!useSameBillingAddress}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Posta Kodu</label>
                          <input
                            type="text"
                            name="billingZipCode"
                            value={formData.billingZipCode}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>
                      </div>
                    </div>
              ) : (
                <p className="text-sm text-gray-500">
                  Fatura adresi farklı olacaksa yukarıdaki işareti kaldırın ve bilgileri girin.
                </p>
              )}
            </div>

            {/* Ödeme Yöntemi */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-4">
                <CreditCard className="w-5 h-5 text-orange-500 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">Ödeme Yöntemi</h2>
              </div>
              
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 mb-3">
                    <strong>Güvenli Ödeme:</strong> Kredi kartı bilgileriniz iyzico'nun güvenli ödeme sayfasında alınacaktır. 
                    "Siparişi Tamamla" butonuna tıkladığınızda güvenli ödeme sayfasına yönlendirileceksiniz.
                  </p>
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                      <span className="text-xs text-gray-700 font-medium whitespace-nowrap">Kabul Edilen Ödeme Yöntemleri:</span>
                      <div className="flex items-center flex-wrap gap-3">
                        <img 
                          src="/images/visa.svg" 
                          alt="Visa" 
                          className="h-6 object-contain flex-shrink-0"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <img 
                          src="/images/mastercard.png" 
                          alt="MasterCard" 
                          className="h-6 object-contain flex-shrink-0"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <img 
                          src="/images/iyzico_logo_band.svg" 
                          alt="iyzico ile öde" 
                          className="h-6 sm:h-8 object-contain max-w-full flex-shrink"
                          style={{ maxWidth: '200px' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Teslimat Yöntemi */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-4">
                <Truck className="w-5 h-5 text-orange-500 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">Teslimat Yöntemi</h2>
              </div>
              
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="deliveryMethod"
                    value="express"
                    checked={formData.deliveryMethod === 'express'}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <span>Hızlı Teslimat İstanbul İçi ( 1 - 4 Saat İçerisinde Teslimat ) - {settings.shipping?.expressShippingCost || 249}₺</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="deliveryMethod"
                    value="standard"
                    checked={formData.deliveryMethod === 'standard'}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <span>Standart Teslimat ( Yurtiçi Kargo ) - Ücretsiz</span>
                </label>
              </div>
            </div>

            {/* Sipariş Notları */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Sipariş Notları</h2>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                placeholder="Siparişinizle ilgili özel notlarınızı buraya yazabilirsiniz..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </div>

          {/* Sağ Taraf - Sipariş Özeti */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Sipariş Özeti</h2>
              
              {/* Ürünler */}
              <div className="space-y-4 mb-6">
                {cartItems && cartItems.map((item) => {
                  // Güvenli image URL alma
                  const getImageUrl = () => {
                    if (!item.product.images || !Array.isArray(item.product.images) || item.product.images.length === 0) {
                      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkdyw7xzw7xsIFlva8SxPC90ZXh0Pjwvc3ZnPg==';
                    }
                    
                    const imagePath = item.product.images[0];
                    
                    // Resim yolu kontrolü
                    if (!imagePath) {
                      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkdyw7xzw7xsIFlva8SxPC90ZXh0Pjwvc3ZnPg==';
                    }
                    
                    // Eğer object ise url property'sini al
                    let actualPath = imagePath;
                    if (typeof imagePath === 'object' && imagePath.url) {
                      actualPath = imagePath.url;
                    } else if (typeof imagePath === 'string') {
                      actualPath = imagePath;
                    } else {
                      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkdyw7xzw7xsIFlva8SxPC90ZXh0Pjwvc3ZnPg==';
                    }
                    
                    // Production'da HTTPS kullan
                    if (process.env.NODE_ENV === 'production') {
                      // HTTPS ile başlamıyorsa HTTPS ekle
                      if (actualPath.startsWith('http://')) {
                        return actualPath.replace('http://', 'https://');
                      }
                      // Relative path ise HTTPS domain ile birleştir
                      if (actualPath.startsWith('/')) {
                        return `https://leasertech.com${actualPath}`;
                      }
                      return `https://leasertech.com/${actualPath}`;
                    }
                    
                    // Development'ta localhost kullan
                    const API_BASE_URL = 'http://localhost:5000';
                    
                    // Eğer dış kaynak URL ise proxy kullan
                    if (actualPath.startsWith('http://') || actualPath.startsWith('https://')) {
                      return `${API_BASE_URL}/proxy-image?url=${encodeURIComponent(actualPath)}`;
                    }
                    
                    // Yerel resim için normal yol
                    const cleanedPath = actualPath.startsWith('/') ? actualPath : `/${actualPath}`;
                    return `${API_BASE_URL}${cleanedPath}`;
                  };
                  
                  return (
                    <div key={item.product_id} className="flex items-center space-x-4">
                      <img
                        src={getImageUrl()}
                        alt={item.product.name}
                        className="w-16 h-16 object-cover rounded-lg"
                        onError={(e) => {
                          console.error('Görsel yüklenemedi:', item.product.name, item.product.images[0]?.url);
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkdyw7xzw7xsIFlva8SxPC90ZXh0Pjwvc3ZnPg==';
                        }}
                      />
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{item.product.name}</h3>
                      <p className="text-sm text-gray-600">Adet: {item.quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">
                        ₺{(item.product.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                    </div>
                  );
                })}
              </div>

              {/* Fiyat Özeti */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Ara Toplam:</span>
                  <span className="font-medium">₺{totalPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Kargo:</span>
                  <span className="font-medium">
                    {formData.deliveryMethod === 'express' 
                      ? `₺${(settings.shipping?.expressShippingCost || 249).toFixed(2)}` 
                      : (totalPrice < (settings.shipping?.freeShippingThreshold || 300) 
                          ? `₺${(settings.shipping?.defaultShippingCost || 50).toFixed(2)}` 
                          : 'Ücretsiz')
                    }
                  </span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Toplam:</span>
                  <span className="text-orange-600">
                    ₺{(() => {
                      let shippingCost = 0;
                      if (formData.deliveryMethod === 'express') {
                        shippingCost = settings.shipping?.expressShippingCost || 249;
                      } else if (formData.deliveryMethod === 'standard') {
                        const threshold = settings.shipping?.freeShippingThreshold || 300;
                        const defaultCost = settings.shipping?.defaultShippingCost || 50;
                        shippingCost = totalPrice < threshold ? defaultCost : 0;
                      }
                      // KDV fiyata dahil, ekstra eklenmeyecek
                      return (totalPrice + shippingCost).toFixed(2);
                    })()}
                  </span>
                </div>
              </div>

              {/* Yasal bilgilendirme */}
              <p className="text-xs text-gray-500 mt-4 leading-relaxed">
                Siparişi tamamlayarak{' '}
                <Link to="/distance-selling" className="text-orange-600 hover:underline">
                  Mesafeli Satış Sözleşmesi
                </Link>
                ,{' '}
                <Link to="/returns" className="text-orange-600 hover:underline">
                  İade & Değişim Politikası
                </Link>
                {' '}ve{' '}
                <Link to="/privacy" className="text-orange-600 hover:underline">
                  Gizlilik Politikası
                </Link>
                &apos;nı okuduğunuzu ve kabul ettiğinizi onaylarsınız.
              </p>

              {/* Sipariş Ver Butonu */}
              <button
                onClick={handleSubmit}
                disabled={loading}
                type="button"
                className={`w-full mt-6 flex items-center justify-center px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-orange-500 hover:bg-orange-600 text-white hover:shadow-lg'
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5 mr-2" />
                    Sipariş Oluşturuluyor...
                  </>
                ) : (
                  'Siparişi Tamamla'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
